import {BehaviorMetric} from 'app_api/projects/metrics/types';
import {EventMenuItem as EventItemValue} from 'common/query-builder/query-block/events-menu/types';
import {FormulaMenuItemValue as FormulaItemValue} from 'common/query-builder/query-block/formulas-menu/types';
import {BehaviorEntity as BehaviorItemValue} from 'app_api/projects/behaviors/types';
import {
  InlineCohort,
  Property as PropertyItemValue,
  SavedCohort,
} from 'common/query-builder/query-block/properties-menu/types';
import {ItemClickEvent, Section, SectionItem} from 'common/components/items-menu/types';
import {MetricType} from 'common/types/reports/bookmark';

export type SavedMetricMenuItemValue = BehaviorMetric;

export enum SidebarItemType {
  Category = `category`,
  EventTag = `event_tag`,
}

export enum SidebarCategory {
  All = `all`,
  Event = `event`,
  Profile = `profile`,
  Formula = `formula`,
  Funnel = `funnel`,
  Retention = `retention`,
  Simple = `simple`,
  Cohort = `cohort`,
  Verified = `verified`,
  'Metric' = `metric`,
}

export type SidebarItem =
  | {type: SidebarItemType.Category; category: SidebarCategory}
  | {type: SidebarItemType.EventTag; tag: string};

export enum MenuType {
  Event = `event`,
  Profile = `profile`,
  Formula = `formula`,
  Funnel = `funnel`,
  Retention = `retention`,
  Cohort = `cohort`,
  SavedMetric = `saved-metric`,
  Simple = `simple`,
}

export type BehaviorMenuTypes = MenuType.Funnel | MenuType.Retention | MenuType.Simple;

export const CategoryToMetricType: {[key in SidebarCategory]?: null | MetricType} = {
  [SidebarCategory.All]: null,
  [SidebarCategory.Event]: MetricType.Event,
  [SidebarCategory.Profile]: MetricType.People,
  [SidebarCategory.Formula]: MetricType.Formula,
  [SidebarCategory.Funnel]: MetricType.Funnel,
  [SidebarCategory.Retention]: MetricType.Retention,
  [SidebarCategory.Simple]: MetricType.Simple,
  [SidebarCategory.Cohort]: MetricType.Cohort,
  // TODO: Technically this category contains MetricType.SavedMEtric and MetricType.Formula.
  // We'd have to refactor CategoryToMetricType to be CategoryToMetricTypes and
  // do checks against the entire list.
  [SidebarCategory.Metric]: MetricType.SavedMetric,
};
export const MenuTypeToMetricType: Record<MenuType, MetricType> = {
  [MenuType.Simple]: MetricType.Simple,
  [MenuType.Event]: MetricType.Event,
  [MenuType.Profile]: MetricType.People,
  [MenuType.Formula]: MetricType.Formula,
  [MenuType.Funnel]: MetricType.Funnel,
  [MenuType.Retention]: MetricType.Retention,
  [MenuType.Cohort]: MetricType.Cohort,
  [MenuType.SavedMetric]: MetricType.SavedMetric,
};

export type ProfileItemValue = `$all_people` | PropertyItemValue;
export type CohortItemValue = InlineCohort | SavedCohort;

export type EventSectionItem = SectionItem<EventItemValue, {menuType: MenuType.Event}>;
export type ProfileSectionItem = SectionItem<ProfileItemValue, {menuType: MenuType.Profile}>;
export type FormulaSectionItem = SectionItem<FormulaItemValue, {menuType: MenuType.Formula}>;
export type SavedMetricSectionItem = SectionItem<BehaviorMetric, {menuType: MenuType.SavedMetric | MenuType.Formula}>;
export type SimpleSectionItem = SectionItem<BehaviorItemValue, {menuType: MenuType.Simple}>;
export type FunnelSectionItem = SectionItem<BehaviorItemValue, {menuType: MenuType.Funnel}>;
export type RetentionSectionItem = SectionItem<BehaviorItemValue, {menuType: MenuType.Retention}>;
export type CohortSectionItem = SectionItem<CohortItemValue, {menuType: MenuType.Cohort}>;

export type BehaviorMenuItem = SimpleSectionItem | FunnelSectionItem | RetentionSectionItem;

export type MultiMetricsMenuItem =
  | EventItemValue
  | ProfileItemValue
  | PropertyItemValue
  | FormulaItemValue
  | SavedMetricMenuItemValue
  | BehaviorItemValue
  | CohortItemValue;
export type MultiMetricsSectionItem = SectionItem<MultiMetricsMenuItem, {menuType: MenuType; label?: any}>;
export type MultiMetricsSection<T = MultiMetricsMenuItem> = Section<T, {menuType: MenuType}> & {
  isRecentSection?: boolean;
};

export type MultiMetricsClickEvent = ItemClickEvent<MultiMetricsMenuItem, MultiMetricsSection, MultiMetricsSectionItem>;

export type MenuTypeMap = {
  [MenuType.Simple]: SimpleSectionItem;
  [MenuType.Event]: EventSectionItem;
  [MenuType.Profile]: ProfileSectionItem;
  [MenuType.Formula]: FormulaSectionItem;
  [MenuType.Funnel]: FunnelSectionItem;
  [MenuType.Retention]: RetentionSectionItem;
  [MenuType.Cohort]: CohortSectionItem;
  [MenuType.SavedMetric]: SavedMetricSectionItem;
};

export function isEventMenuSectionItem(sectionItem: MultiMetricsSectionItem): sectionItem is EventSectionItem {
  return sectionItem.menuType === MenuType.Event;
}

export function isProfileMenuSectionItem(sectionItem: MultiMetricsSectionItem): sectionItem is ProfileSectionItem {
  return sectionItem.menuType === MenuType.Profile;
}

export function isFormulaMenuSectionItem(sectionItem: MultiMetricsSectionItem): sectionItem is FormulaSectionItem {
  return sectionItem.menuType === MenuType.Formula;
}

export function isSavedMetricMenuSectionItem(
  sectionItem: MultiMetricsSectionItem,
): sectionItem is SavedMetricSectionItem {
  return sectionItem.menuType === MenuType.SavedMetric;
}

export function isFunnelMenuSectionItem(sectionItem: MultiMetricsSectionItem): sectionItem is FunnelSectionItem {
  return sectionItem.menuType === MenuType.Funnel;
}

export function isRetentionMenuSectionItem(sectionItem: MultiMetricsSectionItem): sectionItem is RetentionSectionItem {
  return sectionItem.menuType === MenuType.Retention;
}

export function isSimpleMenuSectionItem(sectionItem: MultiMetricsSectionItem): sectionItem is SimpleSectionItem {
  return sectionItem.menuType === MenuType.Simple;
}

export function isCohortMenuSectionItem(sectionItem: MultiMetricsSectionItem): sectionItem is CohortSectionItem {
  return sectionItem.menuType === MenuType.Cohort;
}

export function isBehaviorMenuItem(sectionItem: MultiMetricsSectionItem): sectionItem is BehaviorMenuItem {
  return (
    isFunnelMenuSectionItem(sectionItem) ||
    isRetentionMenuSectionItem(sectionItem) ||
    isSimpleMenuSectionItem(sectionItem)
  );
}
